import { useEffect, useState } from "react";
import { discoverClient, type PublicAppAdmission } from "../discoverClient";
import { exchangeAppViewAccess } from "@/api/appViewAccess";

/** Browser guest admission is tied to the publication, independently of account
 * authentication. Each selection/retry redeems a new one-time view credential.
 */
export function useDiscoverPublicApp(
  listingId: string | null,
  publishedVersion: string | null,
  admission?: PublicAppAdmission | null,
) {
  const viewKey = `${listingId ?? ""}:${publishedVersion ?? ""}`;
  const [retryState, setAttempt] = useState({ key: viewKey, count: 0 });
  const attempt = retryState.key === viewKey ? retryState.count : 0;
  const [result, setResult] = useState<{
    viewKey: string;
    attempt: number;
    appUrl: string | null;
    instanceId: string | null;
    error: string | null;
    expiresAt?: string;
    trialLimitSeconds?: number | null;
  } | null>(null);
  useEffect(() => {
    if (!listingId || admission && attempt === 0) return;
    const controller = new AbortController();
    setResult(null);
    // StrictMode's setup/cleanup/setup can cancel the first effect before any
    // POST is sent. Once issued, late admission cannot replace another app.
    void Promise.resolve()
      .then(() =>
        controller.signal.aborted
          ? null
          : discoverClient.openPublic(listingId, controller.signal),
      )
      .then(
        (opened) => {
          if (opened && !controller.signal.aborted)
            setResult({
              viewKey,
              attempt,
              appUrl: opened.appUrl,
              instanceId: opened.instanceId,
              expiresAt: opened.expiresAt,
              trialLimitSeconds: opened.trialLimitSeconds,
              error: null,
            });
        },
        () => {
          if (!controller.signal.aborted)
            setResult({
              viewKey,
              attempt,
              appUrl: null,
              instanceId: null,
              error: "The public app could not be opened. Try again.",
            });
        },
      );
    return () => controller.abort();
  }, [listingId, viewKey, attempt, admission]);
  const initial = attempt === 0 ? admission : null;
  const current = result?.viewKey === viewKey && result.attempt === attempt ? result : null;
  const admittedUrl = initial ? initial.appUrl : current?.appUrl ?? null;
  const [access, setAccess] = useState<{ source: string; url: string | null; failed: boolean } | null>(null);
  useEffect(() => {
    if (!admittedUrl) return;
    const controller = new AbortController();
    // StrictMode's abandoned first setup must not redeem a one-time grant.
    void Promise.resolve().then(() => controller.signal.aborted ? null :
      exchangeAppViewAccess(admittedUrl, controller.signal)).then(
      (url) => { if (url && !controller.signal.aborted) setAccess({ source: admittedUrl, url, failed: false }); },
      () => { if (!controller.signal.aborted) setAccess({ source: admittedUrl, url: null, failed: true }); },
    );
    return () => controller.abort();
  }, [admittedUrl]);
  const authorized = access?.source === admittedUrl ? access : null;
  return {
    trialLimitSeconds: initial ? initial.trialLimitSeconds : current ? current.trialLimitSeconds : undefined,
    expiresAt: initial ? initial.expiresAt : current ? current.expiresAt : undefined,
    instanceId: initial ? initial.instanceId : current ? current.instanceId : null,
    appUrl: authorized?.url ?? null,
    error: authorized?.failed ? "The public app could not be opened. Try again." : initial ? initial.error && "The public app could not be opened. Try again." : current ? current.error : null,
    retry: () => setAttempt((value) => ({ key: viewKey, count: value.key === viewKey ? value.count + 1 : 1 })),
  };
}
